import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import axiosInstance from "../config/axiosConfig";
import socket from "../config/socket";

const SUPPORT_TOKEN_KEY = "supportToken";
const SUPPORT_ALLOWED_MODULES_KEY = "supportAllowedModules";
const SUPPORT_SESSION_END_KEY = "supportSessionEnd";
const SUPPORT_EMAIL_KEY = "supportEmail";
const SUPPORT_REQUEST_ID_KEY = "supportRequestId";
const DEFAULT_SESSION_MINUTES = 20;

const SupportAuthContext = createContext(null);

const normalizeModules = (modules) => {
  if (!Array.isArray(modules)) return [];
  return modules.map((item) => String(item || "").trim()).filter(Boolean);
};

const resolveSessionEnd = (decoded = {}, responseData = {}) => {
  if (responseData?.sessionEnd) return responseData.sessionEnd;
  if (decoded?.sessionEnd) return decoded.sessionEnd;
  if (decoded?.exp) return new Date(decoded.exp * 1000).toISOString();
  return new Date(Date.now() + DEFAULT_SESSION_MINUTES * 60 * 1000).toISOString();
};

export const SupportAuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const pollTimerRef = useRef(null);

  const [token, setToken] = useState(localStorage.getItem(SUPPORT_TOKEN_KEY) || "");
  const [allowedModules, setAllowedModules] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SUPPORT_ALLOWED_MODULES_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const [sessionEnd, setSessionEnd] = useState(localStorage.getItem(SUPPORT_SESSION_END_KEY) || null);
  const [supportEmail, setSupportEmail] = useState(localStorage.getItem(SUPPORT_EMAIL_KEY) || "");
  const [status, setStatus] = useState(token ? "approved" : "idle");
  const [requestMeta, setRequestMeta] = useState(() => {
    const requestId = localStorage.getItem(SUPPORT_REQUEST_ID_KEY) || "";
    return requestId ? { requestId } : null;
  });
  const [forcedLogoutReason, setForcedLogoutReason] = useState("");

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const persistSession = useCallback((nextToken, nextModules, nextSessionEnd) => {
    localStorage.setItem(SUPPORT_TOKEN_KEY, nextToken);
    // Compatibility: many existing pages still read "token" directly.
    localStorage.setItem("token", nextToken);
    localStorage.setItem(SUPPORT_ALLOWED_MODULES_KEY, JSON.stringify(nextModules));
    localStorage.setItem(SUPPORT_SESSION_END_KEY, nextSessionEnd);
    setToken(nextToken);
    setAllowedModules(nextModules);
    setSessionEnd(nextSessionEnd);
    setStatus("approved");
  }, []);

  const redirectPendingRequestToLogin = useCallback(
    (message = "") => {
      clearPollTimer();
      localStorage.removeItem(SUPPORT_REQUEST_ID_KEY);
      setRequestMeta(null);
      setStatus("idle");
      if (message) {
        setForcedLogoutReason(message);
      }
      navigate("/support/login", { replace: true });
    },
    [clearPollTimer, navigate]
  );

  const logoutSupport = useCallback(
    (reason = "") => {
      clearPollTimer();
      socket.off("support-approved");
      socket.off("support-session-extended");
      socket.off("support-force-logout");
      localStorage.removeItem(SUPPORT_TOKEN_KEY);
      localStorage.removeItem("token");
      localStorage.removeItem(SUPPORT_ALLOWED_MODULES_KEY);
      localStorage.removeItem(SUPPORT_SESSION_END_KEY);
      localStorage.removeItem(SUPPORT_EMAIL_KEY);
      localStorage.removeItem(SUPPORT_REQUEST_ID_KEY);
      setToken("");
      setAllowedModules([]);
      setSessionEnd(null);
      setSupportEmail("");
      setStatus("idle");
      setRequestMeta(null);
      if (reason) {
        setForcedLogoutReason(reason);
      }
      navigate("/support/login", { replace: true });
    },
    [clearPollTimer, navigate]
  );

  const applyApprovedSession = useCallback(
    (payload) => {
      console.log("[SupportAuth] applyApprovedSession payload:", payload);
      const nextToken = payload?.token;
      if (!nextToken) {
        console.warn("[SupportAuth] No token in approval payload. Cannot redirect.");
        return false;
      }
      let decoded = {};
      try {
        decoded = jwtDecode(nextToken);
      } catch {
        decoded = {};
      }
      const modules = normalizeModules(
        payload?.allowedModules || decoded?.allowedModules || decoded?.modules || []
      );
      const endAt = resolveSessionEnd(decoded, payload);
      persistSession(nextToken, modules, endAt);
      console.log("[SupportAuth] Session persisted. Redirecting to /support/dashboard", {
        modules,
        endAt,
      });
      clearPollTimer();
      navigate("/support/dashboard", { replace: true });
      enqueueSnackbar("Support access approved.", { variant: "success" });
      return true;
    },
    [clearPollTimer, enqueueSnackbar, navigate, persistSession]
  );

  const loginSupport = useCallback(
    async ({ email, password }) => {
      console.log("[SupportAuth] Calling POST /support/login", { email });
      const response = await axiosInstance.post("/support/login", { email, password });
      console.log("[SupportAuth] /support/login response:", response?.data);
      localStorage.setItem(SUPPORT_EMAIL_KEY, email);
      setSupportEmail(email);

      if (response?.data?.token) {
        applyApprovedSession(response.data);
        return { approved: true };
      }

      const nextMeta = response?.data || null;
      if (nextMeta?.requestId) {
        localStorage.setItem(SUPPORT_REQUEST_ID_KEY, String(nextMeta.requestId));
      }
      setRequestMeta(nextMeta);
      setStatus("pending");
      console.log("[SupportAuth] Waiting state set", {
        requestId: response?.data?.requestId,
        status: "pending",
      });
      return { approved: false, message: "Waiting for admin approval" };
    },
    [applyApprovedSession]
  );

  const checkApprovalStatus = useCallback(async () => {
    const requestId = requestMeta?.requestId || localStorage.getItem(SUPPORT_REQUEST_ID_KEY) || "";
    const email = supportEmail || localStorage.getItem(SUPPORT_EMAIL_KEY) || "";
    if (!requestId) {
      console.warn("[SupportAuth] checkApprovalStatus skipped: requestId missing.");
      return null;
    }

    try {
      console.log("[SupportAuth] Polling /support/status", { requestId, email });
      const response = await axiosInstance.get("/support/status", {
        params: {
          requestId,
          email: email || undefined,
        },
      });
      console.log("[SupportAuth] /support/status response:", response?.data);

      if (response?.data?.status === "approved" && response?.data?.token) {
        if (status === "pending") {
          applyApprovedSession(response.data);
        } else {
          // Already approved: silently refresh token/sessionEnd/modules (no redirect/toast).
          let decoded = {};
          try {
            decoded = jwtDecode(response.data.token);
          } catch {
            decoded = {};
          }
          const modules = normalizeModules(
            response?.data?.allowedModules || decoded?.allowedModules || allowedModules || []
          );
          const endAt = resolveSessionEnd(decoded, response.data);
          persistSession(response.data.token, modules, endAt);
        }
      } else if (["rejected", "declined"].includes(String(response?.data?.status || "").toLowerCase())) {
        enqueueSnackbar("Support request rejected by admin.", { variant: "error" });
        redirectPendingRequestToLogin(
          "Admin declined your support access request. You can try again after speaking with your administrator."
        );
      }
      return response?.data;
    } catch (error) {
      console.warn("[SupportAuth] /support/status poll failed", error?.response?.data || error?.message);
      return null;
    }
  }, [
    allowedModules,
    applyApprovedSession,
    enqueueSnackbar,
    persistSession,
    redirectPendingRequestToLogin,
    requestMeta?.requestId,
    status,
    supportEmail,
  ]);

  const startApprovalTracking = useCallback(() => {
    if (status !== "pending") return;
    console.log("[SupportAuth] startApprovalTracking: polling every 5 seconds.");
    clearPollTimer();
    pollTimerRef.current = setInterval(() => {
      checkApprovalStatus();
    }, 5000);
  }, [checkApprovalStatus, clearPollTimer, status]);

  const requestMoreTime = useCallback(
    async (durationMinutes = 20) => {
      const duration = Number(durationMinutes);
      const payload = { duration: Number.isFinite(duration) && duration > 0 ? duration : 20 };

      try {
        // Backend-supported endpoint
        await axiosInstance.post("/support/extend", payload);
      } catch (error) {
        // Backward compatibility fallback (older integration path)
        await axiosInstance.post("/support/request-extend", payload);
      }

      enqueueSnackbar("More-time request sent to admin.", { variant: "info" });
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    if (!token) {
      console.log("[SupportAuth] Socket listeners not attached: supportToken missing.");
      return;
    }
    socket.auth = { token };
    if (!socket.connected) socket.connect();
    console.log("[SupportAuth] Socket connected for support session.");

    const onApproved = (payload) => {
      console.log("[SupportAuth] Socket event: support-approved", payload);
      applyApprovedSession(payload);
    };
    const onSessionExtended = (payload) => {
      console.log("[SupportAuth] Socket event: support-session-extended", payload);
      const nextEnd = payload?.sessionEnd;
      if (!nextEnd) return;
      setSessionEnd(nextEnd);
      localStorage.setItem(SUPPORT_SESSION_END_KEY, nextEnd);
      enqueueSnackbar("Support session extended by admin.", { variant: "success" });
    };
    const onForceLogout = (payload) => {
      console.log("[SupportAuth] Socket event: support-force-logout", payload);
      logoutSupport(payload?.message || "Session ended by admin");
    };

    socket.on("support-approved", onApproved);
    socket.on("support-session-extended", onSessionExtended);
    socket.on("support-force-logout", onForceLogout);

    return () => {
      socket.off("support-approved", onApproved);
      socket.off("support-session-extended", onSessionExtended);
      socket.off("support-force-logout", onForceLogout);
    };
  }, [applyApprovedSession, enqueueSnackbar, logoutSupport, token]);

  useEffect(() => () => clearPollTimer(), [clearPollTimer]);

  // Keep approved session metadata (time left/token) in sync after extension approvals.
  useEffect(() => {
    const requestId = requestMeta?.requestId || localStorage.getItem(SUPPORT_REQUEST_ID_KEY) || "";
    if (status !== "approved" || !requestId) return;
    const timer = setInterval(() => {
      checkApprovalStatus();
    }, 10000);
    return () => clearInterval(timer);
  }, [checkApprovalStatus, requestMeta?.requestId, status]);

  const hasModuleAccess = useCallback(
    (moduleName) => {
      const normalized = String(moduleName || "").trim().toLowerCase();
      return allowedModules.some((m) => String(m || "").trim().toLowerCase() === normalized);
    },
    [allowedModules]
  );

  const value = useMemo(
    () => ({
      token,
      allowedModules,
      sessionEnd,
      supportEmail,
      status,
      requestMeta,
      forcedLogoutReason,
      hasModuleAccess,
      setForcedLogoutReason,
      loginSupport,
      checkApprovalStatus,
      startApprovalTracking,
      requestMoreTime,
      logoutSupport,
      setSessionEnd,
    }),
    [
      token,
      allowedModules,
      sessionEnd,
      supportEmail,
      status,
      requestMeta,
      forcedLogoutReason,
      hasModuleAccess,
      loginSupport,
      checkApprovalStatus,
      startApprovalTracking,
      requestMoreTime,
      logoutSupport,
    ]
  );

  return <SupportAuthContext.Provider value={value}>{children}</SupportAuthContext.Provider>;
};

export const useSupportAuth = () => {
  const ctx = useContext(SupportAuthContext);
  if (!ctx) {
    throw new Error("useSupportAuth must be used within SupportAuthProvider");
  }
  return ctx;
};

